import React from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { Icon } from '@iconify/react'
import { BhyveAppBreadcrumbs } from './BhyveAppBreadcrumbs'
import { BhyveHorizontalSubnavV2, type BhyveHorizontalSubnavV2Props } from './BhyveHorizontalSubnavV2'
import { bhyveNavTokens as T } from './bhyveNavTokens'

export type BhyveAppHeaderProps = BhyveHorizontalSubnavV2Props & {
  showPrimaryNavMenuButton?: boolean
  onOpenPrimaryNavMenu?: () => void

  /** a11y: menu drawer open */
  primaryNavMenuOpen?: boolean

  /** Mobile / collapsed rail: breadcrumb strip hidden — title lives on {@link BhyveMobilePrimaryBar}. */
  suppressBreadcrumbs?: boolean
}

/**
 * Global chrome: optional hamburger (narrow) + breadcrumbs + horizontal secondary navigation.
 * Classic `UserLayout` is unchanged.
 */
export const BhyveAppHeader: React.FC<BhyveAppHeaderProps> = ({
  showPrimaryNavMenuButton = false,
  onOpenPrimaryNavMenu,
  primaryNavMenuOpen = false,
  suppressBreadcrumbs = false,
  ...subNavProps
}) => {
  const showMenu = Boolean(showPrimaryNavMenuButton && onOpenPrimaryNavMenu)
  const showBreadcrumbRow = showMenu || !suppressBreadcrumbs

  return (
    <Box
      data-bhyve-app-header
      sx={{
        gridRow: 1,
        zIndex: theme => theme.zIndex.appBar,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        bgcolor: '#fff',
        boxShadow: '0 1px 0 rgba(23, 23, 23, 0.06)'
      }}
    >
      {showBreadcrumbRow ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            minWidth: 0,
            borderBottom: showMenu ? `1px solid ${T.secondaryBarBorder}` : undefined
          }}
        >
          {showMenu ? (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'stretch',

                /** Align with breadcrumb row (`BhyveAppBreadcrumbs` px: 2); avoid flush to viewport edge */
                pl: 2,
                pr: 0.5
              }}
            >
              <IconButton
                color='inherit'
                aria-label='Open navigation menu'
                aria-expanded={primaryNavMenuOpen ? 'true' : 'false'}
                aria-haspopup='dialog'
                size='medium'
                onClick={onOpenPrimaryNavMenu}
                sx={{ color: T.sidebarText, ml: 0 }}
              >
                <Icon icon='tabler:menu-2' width={26} height={26} />
              </IconButton>
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!suppressBreadcrumbs ? <BhyveAppBreadcrumbs suppressBottomBorder={showMenu} /> : null}
          </Box>
        </Box>
      ) : null}
      <BhyveHorizontalSubnavV2 {...subNavProps} />
    </Box>
  )
}
